import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Box, CalendarDays, MapPinned, Menu, Phone, Wrench } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { SITE } from "@/lib/site";
import { Wordmark } from "@/components/common/ui";
import { LifestyleList, LifestyleMenu } from "./LifestyleMenu";
import { GuidesList, GuidesMenu } from "@/components/guides/GuidesMenu";
import { AccountButton } from "./AccountButton";
import { api } from "@/lib/api";

export const NAV = [
  { to: "/search", label: "Buy" },
  { to: "/sellers", label: "Sell", featureId: "sellers" },
  { to: "/bases", label: "Bases in 3D", featureId: "bases", Icon: Box },
  { to: "/military", label: "Military & PCS", featureId: "military" },
  { to: "/buyers", label: "Buyers", featureId: "buyers" },
  { to: "/tools", label: "Tools", featureId: "tools", Icon: Wrench },
  { to: "/neighborhoods", label: "Neighborhood 3D", featureId: "neighborhoods", Icon: MapPinned },
  { to: "/open-houses", label: "Open Houses", featureId: "open-houses", Icon: CalendarDays },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const isOverlayRoute = (p) => ["/", "/military", "/bases", "/neighborhoods"].includes(p) || p.startsWith("/bases/");

const NavItem = ({ n, light }) => (
  <NavLink
    to={n.to}
    data-testid={`nav-${n.to.slice(1)}`}
    className={({ isActive }) =>
      `inline-flex items-center gap-1.5 text-[15px] transition-[background-color,border-color,color,transform] duration-200 ${n.featured ? `rounded-full border px-3 py-2 font-medium hover:-translate-y-px ${light ? "border-white/50 bg-white/10 text-white hover:bg-white hover:text-ink" : "border-brass/70 bg-brass/10 text-ink hover:border-ink hover:bg-ink hover:text-white"}` : `hover:opacity-70 ${light ? "text-white" : "text-ink"}`} ${
        isActive ? "underline decoration-brass decoration-2 underline-offset-[10px]" : ""
      }`
    }
  >
    {n.Icon && <n.Icon className="h-3.5 w-3.5" />}{n.label}
  </NavLink>
);

export const SiteHeader = () => {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [featuredIds, setFeaturedIds] = useState(["bases", "tools", "neighborhoods"]);
  const overlay = isOverlayRoute(pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  useEffect(() => {
    const loadFeatured = () => api.get("/featured-navigation").then(({ data }) => Array.isArray(data?.items) && setFeaturedIds(data.items)).catch(() => {});
    loadFeatured();
    window.addEventListener("featured-navigation-changed", loadFeatured);
    return () => window.removeEventListener("featured-navigation-changed", loadFeatured);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const light = overlay && !scrolled;
  const featuredNav = useMemo(() => featuredIds.map((id) => NAV.find((item) => item.featureId === id)).filter(Boolean).map((item) => ({ ...item, featured: true })), [featuredIds]);
  const regularNav = useMemo(() => NAV.filter((item) => !item.featureId || !featuredIds.includes(item.featureId)), [featuredIds]);
  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 h-16 transition-[background-color,box-shadow] duration-300 ${
        light ? "bg-gradient-to-b from-black/55 to-transparent" : "border-b border-neutral-200 bg-white/95 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-full max-w-[1440px] items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="header-logo-link" aria-label="757 Realty home">
          <Wordmark light={light} />
        </Link>
        <nav className="hidden items-center gap-4 2xl:gap-5 xl:flex" aria-label="Main">
          {regularNav.slice(0, 2).map((n) => <NavItem key={n.to} n={n} light={light} />)}
          <LifestyleMenu light={light} />
          <GuidesMenu light={light} />
          {regularNav.slice(2).map((n) => <NavItem key={n.to} n={n} light={light} />)}
          {featuredNav.map((n) => <NavItem key={n.to} n={n} light={light} />)}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href={SITE.phoneMainHref}
            data-testid="header-call-button"
            className={`hidden items-center gap-2 rounded-full px-4 py-2 text-[15px] font-medium transition-colors 2xl:inline-flex ${
              light ? "bg-white text-ink hover:bg-neutral-100" : "bg-ink text-white hover:bg-neutral-800"
            }`}
          >
            <Phone className="h-4 w-4" /> {SITE.phoneMain}
          </a>
          <a href={SITE.phoneMainHref} aria-label={`Call ${SITE.phoneMain}`} data-testid="header-call-icon"
            className={`hidden h-10 w-10 place-items-center rounded-full sm:grid 2xl:hidden ${light ? "bg-white text-ink" : "bg-ink text-white"}`}>
            <Phone className="h-4 w-4" />
          </a>
          <AccountButton light={light} />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className={`grid h-10 w-10 place-items-center rounded-full xl:hidden ${light ? "text-white" : "text-ink"}`}
                data-testid="mobile-menu-button"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] max-w-[88vw] overflow-y-auto overscroll-contain bg-white" data-testid="mobile-menu">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <Wordmark className="mb-8" />
              <nav className="flex flex-col gap-1">
                {regularNav.filter((n) => n.featureId !== "open-houses").map((n) => (
                  <NavLink key={n.to} to={n.to} data-testid={`mobile-nav-${n.to.slice(1)}`}
                    className={`flex items-center gap-2 text-lg text-ink ${n.featured ? "my-1 rounded-md border border-brass/60 bg-brass/10 px-3 py-3 font-medium transition-[background-color,transform] duration-200 hover:-translate-y-px hover:bg-brass/20" : "border-b border-neutral-100 py-3"}`}>
                    {n.Icon && <n.Icon className="h-4 w-4 text-brass-deep" />}{n.label}
                  </NavLink>
                ))}
                <p className="pt-4 text-lg text-ink">Lifestyle</p>
                {!featuredIds.includes("open-houses") && <NavLink to="/open-houses" data-testid="mobile-nav-open-houses" className="mt-2 flex items-center gap-2 border-b border-neutral-100 py-3 text-lg font-medium text-ink"><CalendarDays className="h-4 w-4 text-brass-deep" />Open Houses</NavLink>}
                <LifestyleList />
                <p className="pt-4 text-lg text-ink">Guides</p>
                <GuidesList />
                <div className="mt-4 border-t border-neutral-200 pt-3" data-testid="mobile-featured-links">
                  {featuredNav.map((n) => (
                    <NavLink key={n.to} to={n.to} data-testid={`mobile-nav-${n.to.slice(1)}`}
                      className="my-1 flex items-center gap-2 rounded-md border border-brass/60 bg-brass/10 px-3 py-3 text-lg font-medium text-ink transition-[background-color,transform] duration-200 hover:-translate-y-px hover:bg-brass/20">
                      {n.Icon && <n.Icon className="h-4 w-4 text-brass-deep" />}{n.label}
                    </NavLink>
                  ))}
                </div>
              </nav>
              <a href={SITE.phoneMainHref} data-testid="mobile-menu-call"
                className="mt-8 flex items-center justify-center gap-2 rounded-full bg-ink py-3 text-white">
                <Phone className="h-4 w-4" /> Call {SITE.phoneMain}
              </a>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
